import { useMemo, useState, useEffect, useCallback } from 'react';
import { getStorage, setStorage, removeStorage } from '@syntra/ui/hooks/use-local-storage';

import { endpoints, axiosInstance } from 'src/utils/axios';

import { LOCAL_STORAGE_KEYS } from 'src/config-global';
import { useAuthContext } from 'src/auth/hooks/use-auth-context';
import type {
  Permission,
  PermissionAction,
  PermissionResource,
} from 'src/domains/business/user-management/types';

import { PermissionsContext } from './permissions-context';

// ----------------------------------------------------------------------

const PERMISSIONS_CACHE_TTL_MS = 10 * 60 * 1000;

const normalizeAction = (action?: string): PermissionAction | null => {
  if (!action) return null;

  const normalized = action === 'get' ? 'read' : action;

  if (
    normalized === 'read' ||
    normalized === 'create' ||
    normalized === 'update' ||
    normalized === 'delete'
  ) {
    return normalized;
  }

  return null;
};

const toPermission = (item: any, fallbackResource?: string): Permission | null => {
  if (!item) return null;

  if (item.value === false) return null;

  const resource = item.resource ?? fallbackResource;
  const action = normalizeAction(item.action);

  if (!resource || !action) return null;

  return {
    id: item.id,
    resource: resource as PermissionResource,
    action,
  };
};

const normalizePermissions = (payload: any): Permission[] => {
  if (!payload) return [];

  const data = payload.data ?? payload;
  const list = Array.isArray(data) ? data : data?.permissions ?? [];

  if (!Array.isArray(list)) return [];

  const permissions: Permission[] = [];

  list.forEach((item) => {
    if (Array.isArray(item?.permissions)) {
      item.permissions.forEach((perm: any) => {
        const permission = toPermission(perm, item.resource);
        if (permission) permissions.push(permission);
      });
      return;
    }

    const permission = toPermission(item);
    if (permission) permissions.push(permission);
  });

  return permissions;
};

type PermissionsCache = {
  permissions: Permission[];
  updatedAt: number;
  userId?: number;
};

type Props = {
  children: React.ReactNode;
};

export function PermissionsProvider({ children }: Props) {
  const { authenticated, loading: authLoading, user } = useAuthContext();

  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const userId = typeof user?.id === 'number' ? user.id : Number(user?.id || 0) || undefined;

  const readCache = useCallback((): PermissionsCache | null => {
    const cached = getStorage(LOCAL_STORAGE_KEYS.permissions) as PermissionsCache | null;

    if (!cached || !Array.isArray(cached.permissions)) return null;
    if (cached.userId && userId && cached.userId !== userId) return null;
    if (Date.now() - cached.updatedAt > PERMISSIONS_CACHE_TTL_MS) return null;

    return cached;
  }, [userId]);

  const fetchPermissions = useCallback(
    async (silent = false) => {
      if (!authenticated) return;

      if (!silent) {
        setLoading(true);
      }

      setError(null);

      try {
        const res = await axiosInstance.get(endpoints.permissions.me);
        const normalized = normalizePermissions(res.data);

        setPermissions(normalized);
        setStorage(LOCAL_STORAGE_KEYS.permissions, {
          permissions: normalized,
          updatedAt: Date.now(),
          userId,
        });
      } catch (err) {
        setError(err);
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [authenticated, userId]
  );

  const refresh = useCallback(async () => {
    await fetchPermissions(false);
  }, [fetchPermissions]);

  useEffect(() => {
    if (authLoading) return;

    if (!authenticated) {
      setPermissions([]);
      setError(null);
      setLoading(false);
      removeStorage(LOCAL_STORAGE_KEYS.permissions);
      return;
    }

    const cached = readCache();

    if (cached?.permissions?.length) {
      setPermissions(cached.permissions);
      setLoading(false);
      fetchPermissions(true);
      return;
    }

    fetchPermissions(false);
  }, [authLoading, authenticated, fetchPermissions, readCache]);
  const hasPermission = useCallback(
    (resource: PermissionResource, action: PermissionAction) =>
      permissions.some(
        (permission) => permission.resource === resource && permission.action === action
      ),
    [permissions]
  );

  const value = useMemo(
    () => ({
      permissions,
      loading: authLoading || loading,
      error,
      refresh,
      hasPermission,
    }),
    [permissions, authLoading, loading, error, refresh, hasPermission]
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}
